import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Plus, Trash2, Calendar, Target, Layers, ChevronDown, ChevronRight, AlertTriangle, Info, Save, X, Edit2, Check } from 'lucide-react';
import { metasAPI, waGroupsAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const MetasConfig = ({ clienteId }) => {
  const { darkMode } = useTheme();

  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planoSelecionado, setPlanoSelecionado] = useState(null);
  const [planoDetalhado, setPlanoDetalhado] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [cidades, setCidades] = useState([]);

  // Modals
  const [showNovoPlano, setShowNovoPlano] = useState(false);
  const [showNovaFase, setShowNovaFase] = useState(false);
  const [showNovoSegmento, setShowNovoSegmento] = useState(null);
  const [editingFase, setEditingFase] = useState(null); // { id, nome, data_inicio, data_fim, meta_fase, baseline_valor }
  const [editingSegmento, setEditingSegmento] = useState(null); // { id, label, meta_valor }
  const [editingPlano, setEditingPlano] = useState(false);

  // Form states
  const [novoPlano, setNovoPlano] = useState({ nome: '', descricao: '', metrica_tipo: 'contatos_unicos', meta_total: '' });
  const [novaFase, setNovaFase] = useState({ nome: '', data_inicio: '', data_fim: '', meta_fase: '', baseline_valor: '' });
  const [novoSegmento, setNovoSegmento] = useState({ label: '', meta_valor: '', filtros: {}, cidade: '' });

  const cardBg = darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200';
  const cardBgInner = darkMode ? 'bg-zinc-900/60 border-gray-700' : 'bg-gray-50 border-gray-200';
  const textMain = darkMode ? 'text-white' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full px-3 py-1.5 rounded-md border text-sm ${
    darkMode ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  } focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none`;

  // ─── Data Loading ────────────────────────────────────────────────────────
  const loadPlanos = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const res = await metasAPI.listarPlanos(clienteId);
      const lista = res.data || [];
      setPlanos(lista);
      if (lista.length === 1 && !planoSelecionado) setPlanoSelecionado(lista[0].id);
    } catch { toast.error('Erro ao carregar planos'); }
    finally { setLoading(false); }
  }, [clienteId]);

  const loadCategorias = useCallback(async () => {
    try {
      const res = await waGroupsAPI.getCategorias();
      setCategorias(res.data?.data || []);
    } catch {}
  }, []);

  const loadCidades = useCallback(async () => {
    if (!clienteId) return;
    try {
      const res = await waGroupsAPI.list(clienteId);
      const grupos = res.data?.groups || res.data || [];
      setCidades([...new Set(grupos.map(g => g.cidade).filter(Boolean))].sort());
    } catch {}
  }, [clienteId]);

  const loadDetalhado = useCallback(async (id) => {
    if (!id) { setPlanoDetalhado(null); return; }
    try {
      const res = await metasAPI.buscarPlano(id);
      setPlanoDetalhado(res.data);
    } catch { toast.error('Erro ao carregar plano'); }
  }, []);

  useEffect(() => { loadPlanos(); loadCategorias(); loadCidades(); }, [loadPlanos, loadCategorias, loadCidades]);
  useEffect(() => { loadDetalhado(planoSelecionado); }, [planoSelecionado, loadDetalhado]);

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleCriarPlano = async (e) => {
    e.preventDefault();
    if (!novoPlano.nome.trim()) return toast.warn('Nome é obrigatório');
    try {
      await metasAPI.criarPlano({ ...novoPlano, cliente_id: clienteId, meta_total: Number(novoPlano.meta_total) || 0 });
      toast.success('Plano criado!');
      setNovoPlano({ nome: '', descricao: '', metrica_tipo: 'contatos_unicos', meta_total: '' });
      setShowNovoPlano(false);
      loadPlanos();
    } catch { toast.error('Erro ao criar plano'); }
  };

  const handleCriarFase = async (e) => {
    e.preventDefault();
    if (!novaFase.nome.trim() || !novaFase.data_inicio || !novaFase.data_fim) return toast.warn('Preencha nome e datas');
    try {
      await metasAPI.criarFase(planoSelecionado, {
        ...novaFase,
        meta_fase: Number(novaFase.meta_fase) || 0,
        baseline_valor: novaFase.baseline_valor !== '' ? Number(novaFase.baseline_valor) : null,
      });
      toast.success('Fase criada!');
      setNovaFase({ nome: '', data_inicio: '', data_fim: '', meta_fase: '', baseline_valor: '' });
      setShowNovaFase(false);
      loadDetalhado(planoSelecionado);
    } catch { toast.error('Erro ao criar fase'); }
  };

  const handleCriarSegmento = async (faseId) => {
    if (!novoSegmento.label.trim()) return toast.warn('Label é obrigatório');
    try {
      const filtros = { ...novoSegmento.filtros };
      if (novoSegmento.cidade) filtros.__cidade = novoSegmento.cidade; // string ou array
      await metasAPI.criarSegmento(faseId, { label: novoSegmento.label, meta_valor: Number(novoSegmento.meta_valor) || 0, filtros });
      toast.success('Segmento criado!');
      setNovoSegmento({ label: '', meta_valor: '', filtros: {}, cidade: '' });
      setShowNovoSegmento(null);
      loadDetalhado(planoSelecionado);
    } catch { toast.error('Erro ao criar segmento'); }
  };

  const handleRemover = async (tipo, id) => {
    if (!window.confirm('Deseja desativar?')) return;
    try {
      if (tipo === 'plano') { await metasAPI.removerPlano(id); setPlanoSelecionado(null); loadPlanos(); }
      else if (tipo === 'fase') { await metasAPI.removerFase(id); loadDetalhado(planoSelecionado); }
      else { await metasAPI.removerSegmento(id); loadDetalhado(planoSelecionado); }
      toast.success('Removido');
    } catch { toast.error('Erro ao remover'); }
  };

  const handleSalvarPlano = async () => {
    if (!planoDetalhado) return;
    try {
      await metasAPI.atualizarPlano(planoSelecionado, {
        nome: planoDetalhado.nome,
        descricao: planoDetalhado.descricao,
        metrica_tipo: planoDetalhado.metrica_tipo,
        meta_total: Number(planoDetalhado.meta_total) || 0,
      });
      toast.success('Plano atualizado');
      setEditingPlano(false);
      loadPlanos();
    } catch { toast.error('Erro ao salvar plano'); }
  };

  const handleSalvarFase = async () => {
    if (!editingFase) return;
    try {
      await metasAPI.atualizarFase(editingFase.id, {
        nome: editingFase.nome,
        data_inicio: editingFase.data_inicio,
        data_fim: editingFase.data_fim,
        meta_fase: Number(editingFase.meta_fase) || 0,
        baseline_valor: editingFase.baseline_valor !== '' && editingFase.baseline_valor != null ? Number(editingFase.baseline_valor) : null,
      });
      toast.success('Fase atualizada');
      setEditingFase(null);
      loadDetalhado(planoSelecionado);
    } catch { toast.error('Erro ao salvar fase'); }
  };

  const handleSalvarSegmento = async () => {
    if (!editingSegmento) return;
    try {
      await metasAPI.atualizarSegmento(editingSegmento.id, {
        label: editingSegmento.label,
        meta_valor: Number(editingSegmento.meta_valor) || 0,
      });
      toast.success('Segmento atualizado');
      setEditingSegmento(null);
      loadDetalhado(planoSelecionado);
    } catch { toast.error('Erro ao salvar segmento'); }
  };

  const handleFiltroChange = (catId, value) => {
    setNovoSegmento((prev) => {
      const filtros = { ...prev.filtros };
      if (!value) {
        delete filtros[catId];
      } else {
        // Toggle: se já existe no array remove, senão adiciona
        const current = filtros[catId];
        if (Array.isArray(current)) {
          if (current.includes(value)) {
            const updated = current.filter(v => v !== value);
            if (updated.length === 0) delete filtros[catId];
            else filtros[catId] = updated;
          } else {
            filtros[catId] = [...current, value];
          }
        } else if (current === value) {
          delete filtros[catId];
        } else if (current) {
          filtros[catId] = [current, value];
        } else {
          filtros[catId] = value;
        }
      }
      return { ...prev, filtros };
    });
  };

  const handleCidadeToggle = (cidade) => {
    setNovoSegmento((prev) => {
      let cidades = Array.isArray(prev.cidade) ? [...prev.cidade] : prev.cidade ? [prev.cidade] : [];
      if (cidades.includes(cidade)) {
        cidades = cidades.filter(c => c !== cidade);
      } else {
        cidades.push(cidade);
      }
      return { ...prev, cidade: cidades.length === 0 ? '' : cidades.length === 1 ? cidades[0] : cidades };
    });
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────
  const fasesSorted = useMemo(() => {
    if (!planoDetalhado?.fases) return [];
    return [...planoDetalhado.fases].sort((a, b) => a.data_inicio.localeCompare(b.data_inicio));
  }, [planoDetalhado]);

  const formatDate = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    return `${day}/${m}`;
  };

  const hoje = new Date().toISOString().slice(0, 10);
  const faseAtiva = (f) => f.data_inicio <= hoje && f.data_fim >= hoje;

  // ─── Render ──────────────────────────────────────────────────────────────
  if (loading) return <div className={`rounded-xl p-8 text-center ${cardBg}`}><p className={textMuted}>Carregando...</p></div>;

  return (
    <div className="space-y-5">
      {/* ─── Header: Seletor de Plano ─────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {planos.length > 0 && (
            <select
              className={`px-3 py-2 rounded-lg border text-sm font-medium ${
                darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              value={planoSelecionado || ''}
              onChange={(e) => { setPlanoSelecionado(e.target.value || null); setEditingPlano(false); }}
            >
              <option value="">Selecione um plano</option>
              {planos.map((p) => (
                <option key={p.id} value={p.id}>{p.nome} — Meta: {(p.meta_total || 0).toLocaleString()}</option>
              ))}
            </select>
          )}
          {planoSelecionado && !editingPlano && (
            <button onClick={() => setEditingPlano(true)} className={`p-1.5 rounded-md ${darkMode ? 'hover:bg-zinc-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} title="Editar plano">
              <Edit2 size={14} />
            </button>
          )}
          {planoSelecionado && (
            <button onClick={() => handleRemover('plano', planoSelecionado)} className="text-red-400 hover:text-red-300 text-xs">
              <Trash2 size={14} />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowNovoPlano(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
        >
          <Plus size={14} /> Novo Plano
        </button>
      </div>

      {/* Editar Plano inline */}
      {editingPlano && planoDetalhado && (
        <div className={`rounded-lg border p-4 space-y-3 ${cardBg}`}>
          <h4 className={`text-xs font-semibold uppercase tracking-wide ${textMuted}`}>Editar Plano</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div><label className={`text-[10px] ${textMuted}`}>Nome</label><input className={inputCls} value={planoDetalhado.nome || ''} onChange={(e) => setPlanoDetalhado({...planoDetalhado, nome: e.target.value})} /></div>
            <div><label className={`text-[10px] ${textMuted}`}>Descrição</label><input className={inputCls} value={planoDetalhado.descricao || ''} onChange={(e) => setPlanoDetalhado({...planoDetalhado, descricao: e.target.value})} /></div>
            <div><label className={`text-[10px] ${textMuted}`}>Métrica</label><select className={inputCls} value={planoDetalhado.metrica_tipo} onChange={(e) => setPlanoDetalhado({...planoDetalhado, metrica_tipo: e.target.value})}><option value="contatos_unicos">Contatos Únicos</option><option value="total_participantes">Total Participantes</option></select></div>
            <div><label className={`text-[10px] ${textMuted}`}>Meta Total</label><input type="number" min="0" className={inputCls} value={planoDetalhado.meta_total || ''} onChange={(e) => setPlanoDetalhado({...planoDetalhado, meta_total: e.target.value})} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setEditingPlano(false); loadDetalhado(planoSelecionado); }} className={`text-xs ${textMuted}`}>Cancelar</button>
            <button onClick={handleSalvarPlano} className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700"><Check size={12} /> Salvar</button>
          </div>
        </div>
      )}

      {/* ─── Modal Novo Plano ─────────────────────────────────────────── */}
      {showNovoPlano && (
        <form onSubmit={handleCriarPlano} className={`rounded-xl border p-5 space-y-4 ${cardBg}`}>
          <div className="flex items-center justify-between">
            <h3 className={`font-semibold ${textMain}`}>Novo Plano de Meta</h3>
            <button type="button" onClick={() => setShowNovoPlano(false)} className={textMuted}><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={`text-xs ${textMuted}`}>Nome *</label><input className={inputCls} placeholder="Eleição 2026" value={novoPlano.nome} onChange={(e) => setNovoPlano({...novoPlano, nome: e.target.value})} /></div>
            <div><label className={`text-xs ${textMuted}`}>Descrição</label><input className={inputCls} placeholder="Opcional" value={novoPlano.descricao} onChange={(e) => setNovoPlano({...novoPlano, descricao: e.target.value})} /></div>
            <div><label className={`text-xs ${textMuted}`}>Métrica</label><select className={inputCls} value={novoPlano.metrica_tipo} onChange={(e) => setNovoPlano({...novoPlano, metrica_tipo: e.target.value})}><option value="contatos_unicos">Contatos Únicos</option><option value="total_participantes">Total Participantes</option></select></div>
            <div><label className={`text-xs ${textMuted}`}>Meta Total</label><input type="number" min="0" className={inputCls} placeholder="75000" value={novoPlano.meta_total} onChange={(e) => setNovoPlano({...novoPlano, meta_total: e.target.value})} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowNovoPlano(false)} className={`px-3 py-1.5 text-sm rounded-md ${textMuted}`}>Cancelar</button>
            <button type="submit" className="px-4 py-1.5 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700">Criar Plano</button>
          </div>
        </form>
      )}

      {/* ─── Timeline de Fases ────────────────────────────────────────── */}
      {planoSelecionado && planoDetalhado && (
        <div className="space-y-4">
          {/* Header das fases */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-blue-500" />
              <h3 className={`text-sm font-semibold ${textMain}`}>Fases — Timeline</h3>
            </div>
            <button
              onClick={() => setShowNovaFase(true)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              <Plus size={12} /> Nova Fase
            </button>
          </div>

          {/* Form Nova Fase */}
          {showNovaFase && (
            <form onSubmit={handleCriarFase} className={`rounded-lg border p-4 space-y-3 ${cardBgInner}`}>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div><label className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Nome</label><input className={inputCls} placeholder="Fase 2A" value={novaFase.nome} onChange={(e) => setNovaFase({...novaFase, nome: e.target.value})} /></div>
                <div><label className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Início</label><input type="date" className={inputCls} value={novaFase.data_inicio} onChange={(e) => setNovaFase({...novaFase, data_inicio: e.target.value})} /></div>
                <div><label className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Fim</label><input type="date" className={inputCls} value={novaFase.data_fim} onChange={(e) => setNovaFase({...novaFase, data_fim: e.target.value})} /></div>
                <div><label className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Meta</label><input type="number" min="0" className={inputCls} placeholder="12000" value={novaFase.meta_fase} onChange={(e) => setNovaFase({...novaFase, meta_fase: e.target.value})} /></div>
                <div>
                  <label className={`text-[10px] uppercase tracking-wide ${textMuted} flex items-center gap-1`}>
                    Baseline
                    <span title="Valor inicial. Progresso = atual − baseline" className="cursor-help">
                      <Info size={10} className="text-blue-400" />
                    </span>
                  </label>
                  <input type="number" min="0" className={inputCls} placeholder="Auto" value={novaFase.baseline_valor} onChange={(e) => setNovaFase({...novaFase, baseline_valor: e.target.value})} />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowNovaFase(false)} className={`text-xs ${textMuted}`}>Cancelar</button>
                <button type="submit" className="px-3 py-1 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700">Criar</button>
              </div>
            </form>
          )}

          {/* Fases como Timeline Cards */}
          {fasesSorted.length === 0 && !showNovaFase && (
            <div className={`rounded-xl border border-dashed p-6 text-center ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
              <Calendar size={24} className={`mx-auto mb-2 ${textMuted}`} />
              <p className={`text-sm ${textMuted}`}>Nenhuma fase cadastrada. Clique em "Nova Fase" para começar.</p>
            </div>
          )}

          <div className="space-y-3">
            {fasesSorted.map((fase) => {
              const isAtiva = faseAtiva(fase);
              const somaSegs = (fase.segmentos || []).reduce((s, seg) => s + (seg.meta_valor || 0), 0);
              const diverge = somaSegs > 0 && somaSegs !== fase.meta_fase;

              return (
                <div key={fase.id} className={`rounded-xl border overflow-hidden ${isAtiva ? (darkMode ? 'border-blue-500/50 ring-1 ring-blue-500/20' : 'border-blue-400 ring-1 ring-blue-100') : (darkMode ? 'border-gray-700' : 'border-gray-200')}`}>
                  {/* Fase Header */}
                  <div className={`flex items-center justify-between px-4 py-3 ${isAtiva ? (darkMode ? 'bg-blue-900/20' : 'bg-blue-50') : (darkMode ? 'bg-zinc-800' : 'bg-white')}`}>
                    <div className="flex items-center gap-3">
                      {/* Indicador visual de timeline */}
                      <div className={`w-2.5 h-2.5 rounded-full ${isAtiva ? 'bg-blue-500 animate-pulse' : (darkMode ? 'bg-gray-600' : 'bg-gray-300')}`} />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${textMain}`}>{fase.nome}</span>
                          {isAtiva && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-medium">Em andamento</span>}
                        </div>
                        <div className={`text-xs ${textMuted} flex items-center gap-2 mt-0.5`}>
                          <span>{formatDate(fase.data_inicio)} — {formatDate(fase.data_fim)}</span>
                          <span>·</span>
                          <span className="font-medium">Meta: {(fase.meta_fase || 0).toLocaleString()}</span>
                          {fase.baseline_valor != null && <><span>·</span><span>Base: {fase.baseline_valor.toLocaleString()}</span></>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowNovoSegmento(showNovoSegmento === fase.id ? null : fase.id)}
                        className={`flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        <Plus size={10} /> Segmento
                      </button>
                      <button
                        onClick={() => setEditingFase(editingFase?.id === fase.id ? null : { id: fase.id, nome: fase.nome, data_inicio: fase.data_inicio, data_fim: fase.data_fim, meta_fase: fase.meta_fase, baseline_valor: fase.baseline_valor ?? '' })}
                        className={`p-1 rounded ${darkMode ? 'hover:bg-zinc-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                        title="Editar fase"
                      >
                        <Edit2 size={12} />
                      </button>
                      <button onClick={() => handleRemover('fase', fase.id)} className="p-1 rounded text-red-400 hover:bg-red-900/20"><Trash2 size={13} /></button>
                    </div>
                  </div>

                  {/* Segmentos */}
                  <div className={`px-4 pb-3 pt-2 ${darkMode ? 'bg-zinc-800/50' : 'bg-white'}`}>
                    {/* Edição inline da fase */}
                    {editingFase?.id === fase.id && (
                      <div className={`rounded-lg border p-3 mb-3 space-y-2 ${cardBgInner}`}>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                          <div><label className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Nome</label><input className={inputCls} value={editingFase.nome} onChange={(e) => setEditingFase({...editingFase, nome: e.target.value})} /></div>
                          <div><label className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Início</label><input type="date" className={inputCls} value={editingFase.data_inicio} onChange={(e) => setEditingFase({...editingFase, data_inicio: e.target.value})} /></div>
                          <div><label className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Fim</label><input type="date" className={inputCls} value={editingFase.data_fim} onChange={(e) => setEditingFase({...editingFase, data_fim: e.target.value})} /></div>
                          <div><label className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Meta</label><input type="number" min="0" className={inputCls} value={editingFase.meta_fase} onChange={(e) => setEditingFase({...editingFase, meta_fase: e.target.value})} /></div>
                          <div><label className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Baseline</label><input type="number" min="0" className={inputCls} placeholder="Auto" value={editingFase.baseline_valor} onChange={(e) => setEditingFase({...editingFase, baseline_valor: e.target.value})} /></div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setEditingFase(null)} className={`text-xs ${textMuted}`}>Cancelar</button>
                          <button onClick={handleSalvarFase} className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700"><Check size={11} /> Salvar</button>
                        </div>
                      </div>
                    )}
                    {/* Warning divergência */}
                    {diverge && (
                      <div className={`flex items-center gap-1.5 text-[11px] mb-2 px-2 py-1 rounded ${darkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-50 text-yellow-700'}`}>
                        <AlertTriangle size={11} />
                        Soma segmentos ({somaSegs.toLocaleString()}) ≠ meta fase ({(fase.meta_fase || 0).toLocaleString()})
                      </div>
                    )}

                    {/* Form Novo Segmento inline */}
                    {showNovoSegmento === fase.id && (
                      <div className={`rounded-lg border p-3 mb-2 space-y-2 ${cardBgInner}`}>
                        <div className="grid grid-cols-4 gap-2">
                          <div><label className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Label</label><input className={inputCls} placeholder="Campinas" value={novoSegmento.label} onChange={(e) => setNovoSegmento({...novoSegmento, label: e.target.value})} /></div>
                          <div><label className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Meta</label><input type="number" min="0" className={inputCls} placeholder="10500" value={novoSegmento.meta_valor} onChange={(e) => setNovoSegmento({...novoSegmento, meta_valor: e.target.value})} /></div>
                          <div>
                            <label className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Cidade</label>
                            <div className={`flex flex-wrap gap-1 mt-0.5 p-1.5 rounded-md border max-h-24 overflow-y-auto ${darkMode ? 'bg-zinc-700 border-gray-600' : 'bg-white border-gray-300'}`}>
                              {cidades.map((c) => {
                                const selected = Array.isArray(novoSegmento.cidade) ? novoSegmento.cidade.includes(c) : novoSegmento.cidade === c;
                                return (
                                  <button type="button" key={c} onClick={() => handleCidadeToggle(c)}
                                    className={`px-1.5 py-0.5 rounded text-[10px] transition-colors ${
                                      selected ? 'bg-blue-600 text-white' : (darkMode ? 'bg-zinc-600 text-gray-300 hover:bg-zinc-500' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                                    }`}>{c}</button>
                                );
                              })}
                            </div>
                          </div>
                          {categorias.map((cat) => (
                            <div key={cat.id}>
                              <label className={`text-[10px] uppercase tracking-wide ${textMuted}`}>{cat.label}</label>
                              <div className={`flex flex-wrap gap-1 mt-0.5 p-1.5 rounded-md border max-h-24 overflow-y-auto ${darkMode ? 'bg-zinc-700 border-gray-600' : 'bg-white border-gray-300'}`}>
                                <button type="button" onClick={() => handleFiltroChange(cat.id, '__none__')}
                                  className={`px-1.5 py-0.5 rounded text-[10px] transition-colors ${
                                    (Array.isArray(novoSegmento.filtros[cat.id]) ? novoSegmento.filtros[cat.id].includes('__none__') : novoSegmento.filtros[cat.id] === '__none__')
                                      ? 'bg-blue-600 text-white' : (darkMode ? 'bg-zinc-600 text-gray-300 hover:bg-zinc-500' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                                  }`}>Sem valor</button>
                                {(cat.opcoes || []).map((op) => (
                                  <button type="button" key={op.id} onClick={() => handleFiltroChange(cat.id, op.id)}
                                    className={`px-1.5 py-0.5 rounded text-[10px] transition-colors ${
                                      (Array.isArray(novoSegmento.filtros[cat.id]) ? novoSegmento.filtros[cat.id].includes(op.id) : novoSegmento.filtros[cat.id] === op.id)
                                        ? 'bg-blue-600 text-white' : (darkMode ? 'bg-zinc-600 text-gray-300 hover:bg-zinc-500' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                                    }`}>{op.nome}</button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                          <button onClick={() => setShowNovoSegmento(null)} className={`text-xs ${textMuted}`}>Cancelar</button>
                          <button onClick={() => handleCriarSegmento(fase.id)} className="px-3 py-1 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700">Criar</button>
                        </div>
                      </div>
                    )}

                    {/* Lista de segmentos como chips/tags */}
                    {(fase.segmentos || []).length === 0 && showNovoSegmento !== fase.id ? (
                      <p className={`text-xs ${textMuted} py-1`}>Sem segmentos — clique em "+ Segmento" para dividir a meta por região/filtro.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {(fase.segmentos || []).map((seg) => (
                          editingSegmento?.id === seg.id ? (
                            <div key={seg.id} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs border ${darkMode ? 'bg-zinc-700 border-blue-500' : 'bg-blue-50 border-blue-300'}`}>
                              <input className={`w-24 px-1.5 py-0.5 rounded text-xs ${darkMode ? 'bg-zinc-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'} border`} value={editingSegmento.label} onChange={(e) => setEditingSegmento({...editingSegmento, label: e.target.value})} />
                              <input type="number" min="0" className={`w-20 px-1.5 py-0.5 rounded text-xs ${darkMode ? 'bg-zinc-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'} border`} value={editingSegmento.meta_valor} onChange={(e) => setEditingSegmento({...editingSegmento, meta_valor: e.target.value})} />
                              <button onClick={handleSalvarSegmento} className="text-green-400 hover:text-green-300"><Check size={12} /></button>
                              <button onClick={() => setEditingSegmento(null)} className={textMuted}><X size={12} /></button>
                            </div>
                          ) : (
                            <div
                              key={seg.id}
                              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs border cursor-pointer transition-colors ${
                                darkMode ? 'bg-zinc-700/50 border-gray-600 hover:border-gray-500' : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                              }`}
                              onClick={() => setEditingSegmento({ id: seg.id, label: seg.label, meta_valor: seg.meta_valor || 0 })}
                              title="Clique para editar"
                            >
                              <Layers size={11} className="text-blue-400" />
                              <span className={`font-medium ${textMain}`}>{seg.label}</span>
                              <span className={textMuted}>{(seg.meta_valor || 0).toLocaleString()}</span>
                              {seg.filtros && Object.keys(seg.filtros).length > 0 && (
                                <span className={`text-[10px] px-1 py-0.5 rounded ${darkMode ? 'bg-zinc-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                                  {Object.keys(seg.filtros).length} filtro{Object.keys(seg.filtros).length > 1 ? 's' : ''}
                                </span>
                              )}
                              <button onClick={(e) => { e.stopPropagation(); handleRemover('segmento', seg.id); }} className="text-red-400 hover:text-red-300 ml-1"><X size={11} /></button>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Estado vazio */}
      {!planoSelecionado && planos.length === 0 && !showNovoPlano && (
        <div className={`rounded-xl border border-dashed p-8 text-center ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
          <Target size={32} className={`mx-auto mb-3 ${textMuted}`} />
          <p className={`font-medium ${textMain}`}>Nenhum plano de meta</p>
          <p className={`text-sm ${textMuted} mt-1`}>Crie um plano para começar a definir fases e metas por segmento.</p>
        </div>
      )}
    </div>
  );
};

export default MetasConfig;
